import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import weakKey from 'weak-key';

import { menuUtils } from '../../../pages/RestaurantMenu/utils';
import {
  BasketActionsType,
  BasketItemType,
  RemoveBasketItemType,
} from '../../../types/basket';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { PriceTag, QuantityControl, Typography } from '../../atoms';

export interface BasketItemProps {
  actions: BasketActionsType;
  currency: Api.Currency;
  languageCode: string;
  basketItem: BasketItemType;
  menuId: number;
  translate: any;
  vrId: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  basketItem: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0),

    '&:last-of-type': {
      borderBottom: 'none',
    },
  },

  basketContent: {
    paddingRight: theme.spacing(1),
  },

  itemName: {
    marginBottom: theme.spacing(1),
  },

  itemOption: {
    color: theme.palette.grey[600],
  },

  basketControls: {
    minWidth: '100px',

    '& > :first-child': {
      marginBottom: theme.spacing(1),
    },
  },
}));

export const BasketItem = (props: BasketItemProps): JSX.Element => {
  const classes = useStyles();
  const {
    actions,
    currency,
    languageCode,
    basketItem,
    menuId,
    translate,
    vrId,
  } = props;

  const { item, options, quantity } = basketItem;
  const { handleAddItem, handleRemoveItem } = menuUtils;

  const itemDetails = options.length
    ? options.map((option) => option.name)
    : [item.Description];
  const total = item.Price * quantity;
  const localisedTotal = BasketControl.ToCurrency(
    total,
    currency,
    languageCode,
  );

  const onHandleAddItem = () => {
    const addItemData = {
      Item: basketItem.item,
      Options: basketItem.options,
    } as BasketControl.Item;

    return handleAddItem(actions.onAddItem, addItemData, menuId, vrId);
  };
  const onHandleRemoveItem = () => {
    const removeItemData = {
      index: basketItem.index,
      item: basketItem.item,
      menuId: menuId,
    } as RemoveBasketItemType;

    return handleRemoveItem(actions.onRemoveItem, removeItemData, vrId);
  };

  return (
    <li
      aria-label={`${quantity} ${item.Name} @ ${localisedTotal}`}
      className={classes.basketItem}
    >
      <div className={classes.basketContent}>
        <Typography className={classes.itemName} variant="h3">
          {item.Name}
        </Typography>

        {itemDetails.map((item) => (
          <Typography
            className={classes.itemOption}
            key={weakKey({ item })}
            variant="body2"
          >
            {item}
          </Typography>
        ))}
      </div>

      <div className={classes.basketControls}>
        <PriceTag
          hasMenuOptions={false}
          localisedPrice={localisedTotal}
          translate={translate}
          variant="right"
        />
        <QuantityControl
          itemName={item.Name}
          onAddItem={onHandleAddItem}
          onRemoveItem={onHandleRemoveItem}
          quantity={quantity}
        />
      </div>
    </li>
  );
};
